import {
  parseDateFilterEntries,
  type DateFilterEntry,
} from '@/lib/dateFilterEntries';

/**
 * Turns the Sent-date filter's `gte:`/`lte:`/`eq:` entries into the absolute
 * instants sent to the API.
 *
 * The picker emits a plain calendar date (`20260730`) — the day the user sees
 * in their own timezone. So the bounds are resolved HERE, in the browser: the
 * lower bound is local midnight and the upper bound is the last millisecond of
 * that local day, both serialized as UTC instants. Picking the same date for
 * From and To therefore covers that user's whole 24-hour day, and someone in
 * Vancouver and someone in Toronto each get their own day rather than a UTC one.
 *
 * Resolving it on the server instead would mean guessing: the server has no
 * idea which day the user was looking at.
 *
 * An entry that carries a time (`202607301430`) is that local time exactly —
 * no widening to the end of the day.
 */

/**
 * A validated entry in the browser's timezone → a UTC instant. A date with no
 * time becomes the start of that local day, or its final millisecond when it is
 * an upper bound.
 */
function toInstant(entry: DateFilterEntry, bound: 'start' | 'end'): string {
  const { year, monthIndex, day, hour, minute } = entry;

  if (hour !== undefined) {
    return new Date(year, monthIndex, day, hour, minute).toISOString();
  }

  return bound === 'end'
    ? new Date(year, monthIndex, day, 23, 59, 59, 999).toISOString()
    : new Date(year, monthIndex, day).toISOString();
}

/**
 * The picker's raw `YYYYMMDD` / `YYYYMMDDHHMM` values, for the URL.
 *
 * The URL keeps the calendar date the user picked, NOT the resolved instant —
 * an instant would be re-read as a local date and drift a day each time the
 * filter round-trips through the URL.
 */
export function rawDateFilterValues(values: string[]): {
  from?: string;
  to?: string;
} {
  let from: string | undefined;
  let to: string | undefined;
  for (const entry of parseDateFilterEntries(values)) {
    if (entry.op === 'gte' || entry.op === 'eq') from = entry.value;
    if (entry.op === 'lte' || entry.op === 'eq') to = entry.value;
  }
  return { from, to };
}

/** The instants to filter on, resolved in the browser's timezone. */
export function parseDateFilterValues(values: string[]): {
  from?: string;
  to?: string;
} {
  let from: string | undefined;
  let to: string | undefined;
  for (const entry of parseDateFilterEntries(values)) {
    if (entry.op === 'gte' || entry.op === 'eq')
      from = toInstant(entry, 'start');
    if (entry.op === 'lte' || entry.op === 'eq') to = toInstant(entry, 'end');
  }
  return { from, to };
}
